/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { subDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import useAccountPageParams from "../../../../hooks/useAccountPageParams";
import { Select, Paper, Group, Stack, LoadingOverlay } from "@mantine/core";
import { ChevronDown, TrendingUp, X } from "lucide-react";
import { getCompanyMetrics } from "../../../../services/companyEndpoint";
import { getTraitHistory } from "../../../../services/computedTraits/traitHistoryEndpoints";
import dateConverter, { dateFormatter } from "../../../../utils/dateConverter";
import TrendsChart, { COLORS } from "./components/TrendsChart";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { useWorkspaceObjectSchema } from "../../../../hooks/QueryHooks";
import MultiSelectDropdown from "../../../../shared/ui/dropdown/MultiSelectDropdown";

const DURATION_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 180 days" },
  { value: "365", label: "Last 365 days" },
];

const AccountTrends = () => {
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const params: any = useAccountPageParams();

  const [duration, setDuration] = useState("30");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({
    workspaceId: workspaceId || "",
    enabled: !!workspaceId,
  });

  const { eventMetrics, computedTraitsList, allItemsByName } = useMemo(() => {
    // After the buildWorkspaceObjectSchema fix, computed traits (even those with
    // attribute_type: "metric_traits") are always in fieldsByObjectSlug, never in metrics.
    // So metrics.list contains only real event metrics (type: "metric" from the API).
    const allMetrics: any[] = workspaceSchema?.metrics?.list || [];
    const accountFields: any[] = workspaceSchema?.fieldsByObjectSlug?.accounts?.list || [];

    // 1. Event metrics: real events like metric_dashboard_viewed, metric_trial_started
    //    Exclude default_metric_* (system defaults)
    const events = allMetrics
      .filter((m: any) =>
        !m.name?.startsWith("default_metric_")
        && !m.is_system
      )
      .map((m: any) => ({
        ...m,
        attribute_type: "metric_traits",
        _source: "event_metric",
      }));

    // 2. All computed traits from account fields (with and without history)
    const seenNames = new Set<string>();
    const allComputedTraits: any[] = [];

    accountFields
      .filter((f: any) => f.generation_type === "computed_trait")
      .forEach((f: any) => {
        const bareName = f.field_name || f.raw_name || f.column_name || "";
        const name = bareName.startsWith("c__") ? bareName : `c__${bareName}`;
        if (seenNames.has(name)) return;
        seenNames.add(name);
        allComputedTraits.push({
          ...f,
          name,
          display_name: f.display_name || bareName,
          attribute_type: "computed_traits",
          _source: "computed_trait",
          source: f.source || "native",
        });
      });

    const all = [...events, ...allComputedTraits];
    const byName: Record<string, any> = {};
    all.forEach((item) => { byName[item.name] = item; });

    return {
      eventMetrics: events,
      computedTraitsList: allComputedTraits,
      allItemsByName: byName,
    };
  }, [workspaceSchema?.metrics?.list, workspaceSchema?.fieldsByObjectSlug?.accounts?.list]);

  // Handle item selection from MultiSelectDropdown
  const handleSelectItem = (item: any) => {
    setSelectedItems((prev) => {
      if (prev.includes(item.name)) {
        return prev.filter((n) => n !== item.name);
      }
      return [...prev, item.name];
    });
  };

  // Fetch trend data when selection changes
  useEffect(() => {
    if (!selectedItems?.length || !params.accountId || !workspaceId) {
      setData([]);
      return;
    }

    const fetchTrends = async () => {
      setIsLoading(true);
      const days = parseInt(duration, 10);

      try {
        // Split into event metrics and computed traits
        const eventMetricNames = selectedItems.filter((s) => !s.startsWith("c__"));
        const computedTraitNames = selectedItems.filter((s) => s.startsWith("c__"));

        const dateMap = new Map<string, any>();

        // Fetch event metrics via getCompanyMetrics (same as TrendsContainer)
        if (eventMetricNames.length > 0) {
          const metricPromises = eventMetricNames.map((eventName) =>
            getCompanyMetrics({
              bucket_size: "day",
              external_id: params.accountId,
              event_name: [eventName],
              workspace_identifier: workspaceId,
              from_date: dateConverter(subDays(new Date(), days)),
            })
          );
          const metricResults = await Promise.all(metricPromises);

          metricResults.forEach((response: any, idx: number) => {
            const metricName = eventMetricNames[idx];
            const label = allItemsByName[metricName]?.display_name || metricName;
            const histogramValues = response?.data?.[metricName]?.histogram_values || [];

            if (histogramValues.length > 0) {
              histogramValues.forEach((h: any) => {
                const formattedDate = dateFormatter(h.date);
                const existing = dateMap.get(formattedDate) || { date: formattedDate };
                existing[label] = h.count ?? 0;
                dateMap.set(formattedDate, existing);
              });
            } else {
              // No histogram data — generate zero-filled date range so chart still renders
              for (let i = days; i >= 0; i--) {
                const formattedDate = dateFormatter(subDays(new Date(), i).toISOString());
                const existing = dateMap.get(formattedDate) || { date: formattedDate };
                existing[label] = 0;
                dateMap.set(formattedDate, existing);
              }
            }
          });
        }

        // Fetch computed traits via getTraitHistory
        if (computedTraitNames.length > 0) {
          const traitPromises = computedTraitNames.map((traitKey) => {
            return getTraitHistory({
              workspace_id: workspaceId,
              entity_type: "accounts",
              record_id: params.accountId,
              trait_name: traitKey,
              days,
            }).then((response) => ({
              traitKey,
              history: response?.data?.history || [],
            }));
          });
          const traitResults = await Promise.all(traitPromises);

          traitResults.forEach(({ traitKey, history }) => {
            const label = allItemsByName[traitKey]?.display_name || traitKey;
            if (history.length > 0) {
              history.forEach((h: any) => {
                const formattedDate = dateFormatter(h.snapshot_date || h.date);
                const existing = dateMap.get(formattedDate) || { date: formattedDate };
                existing[label] = h.value_numeric ?? h.value ?? h.count ?? 0;
                dateMap.set(formattedDate, existing);
              });
            } else {
              // No history data — generate zero-filled date range so chart still renders
              for (let i = days; i >= 0; i--) {
                const formattedDate = dateFormatter(subDays(new Date(), i).toISOString());
                const existing = dateMap.get(formattedDate) || { date: formattedDate };
                existing[label] = 0;
                dateMap.set(formattedDate, existing);
              }
            }
          });
        }

        setData(
          Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date))
        );
      } catch (e) {
        console.error("Error fetching trend data:", e);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrends();
  }, [params.accountId, selectedItems, duration, workspaceId, allItemsByName]);

  // Selected item labels for the trigger button
  const selectedLabels = selectedItems
    .map((name) => allItemsByName[name]?.display_name || name)
    .filter(Boolean);

  return (
    <Stack gap="md" className="h-full p-4">
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-end">
          <div className="flex-1 min-w-[300px]">
            <div className="text-sm font-medium text-gray-700 mb-1">Select metrics</div>
            <MultiSelectDropdown
              list={[]}
              successMetricsList={computedTraitsList}
              metricsList={eventMetrics}
              selected={selectedItems}
              setSelected={handleSelectItem}
              isOpen={pickerOpen}
              setIsOpen={setPickerOpen}
              type="Accounts"
              position="bottom-start"
            >
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-left min-h-[36px]"
              >
                <span className={selectedLabels.length > 0 ? "text-gray-900 truncate" : "text-gray-400"}>
                  {selectedLabels.length > 0
                    ? selectedLabels.join(", ")
                    : "Select metrics to plot..."}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </button>
            </MultiSelectDropdown>
          </div>
          <Select
            label="Duration"
            value={duration}
            onChange={(value) => {
              if (value) setDuration(value);
            }}
            data={DURATION_OPTIONS}
            size="sm"
            classNames={{ root: "w-[160px]" }}
          />
        </Group>
      </Paper>

      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedItems.map((name, idx) => {
            const label = allItemsByName[name]?.display_name || name;
            const color = idx < COLORS.length ? COLORS[idx] : "#6B7280";
            return (
              <span
                key={name}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm"
              >
                <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                {label}
                <button
                  type="button"
                  onClick={() => setSelectedItems((prev) => prev.filter((n) => n !== name))}
                  className="ml-0.5 rounded-full p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <Paper p="md" radius="md" withBorder className="flex-1 min-h-[400px] relative">
        <LoadingOverlay visible={isLoading} loaderProps={{ size: "md" }} overlayProps={{ blur: 2 }} />
        {selectedItems.length > 0 && !isLoading ? (
          <TrendsChart data={data} bucket="day" />
        ) : !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full py-16 text-center">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
              <TrendingUp className="h-6 w-6 text-gray-400" />
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              {(eventMetrics.length > 0 || computedTraitsList.length > 0)
                ? "Select a metric to view trends"
                : "No metrics available"}
            </h4>
            <p className="text-xs text-gray-500 max-w-[280px]">
              {(eventMetrics.length > 0 || computedTraitsList.length > 0)
                ? "Choose events or computed traits from the dropdown to visualize trends over time."
                : "No events or computed traits found for this workspace."}
            </p>
          </div>
        ) : null}
      </Paper>
    </Stack>
  );
};

export default AccountTrends;
